/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import React, { createContext, useContext, useEffect, useState } from "react"
import mixpanel from "mixpanel-browser"
import { merge } from "lodash"
import { DEFAULT_CLIENT_SLUG } from "@client-hub/store/constants"
import { useClientConfigurationQuery } from "@client-hub/store/api/crs/react"
import { getEnvVar } from "@client-hub/shared/utils/env"
import { EnvironmentVariable } from "@client-hub/shared/constants/env"

export type TrackEventPayload = {
  appName?: string
  route?: string
  action?: string
  properties?: { [k: string]: any }
}

export interface AnalyticsContextType {
  trackEvent: (eventPayload: TrackEventPayload) => void
}

export const AnalyticsContext = createContext<AnalyticsContextType>({
  trackEvent: () => {
    console.warn("Analytics not initialized")
  },
})

export const AnalyticsProvider: React.FC = ({ children }) => {
  const [clientSlug, setClientSlug] = useState<string>("")

  useEffect(() => {
    const mixpanelToken = getEnvVar(EnvironmentVariable.MIXPANEL_TOKEN)
    if (mixpanelToken) {
      mixpanel.init(mixpanelToken)
    }
  }, [])

  const {
    data: clientConfigurationData,
    isFetching: isFetchingConfigurationData,
    isUninitialized,
  } = useClientConfigurationQuery()

  useEffect(() => {
    if (!isFetchingConfigurationData && !isUninitialized && clientConfigurationData) {
      setClientSlug(clientConfigurationData?.client_slug)
    }
  }, [isFetchingConfigurationData, isUninitialized])

  const trackEvent = (eventPayload: TrackEventPayload) => {
    const { appName, route, action } = eventPayload
    const generatedEvent = [appName, route, action].filter(Boolean).join(" > ")

    if (!getEnvVar(EnvironmentVariable.MIXPANEL_TOKEN)) {
      console.warn(`Cannot track event: ${generatedEvent}. Mixpanel token not configured`)
      return
    }
    const mixpanelProperties = {
      name: generatedEvent,
      properties: merge({}, { client: clientSlug || DEFAULT_CLIENT_SLUG }, eventPayload.properties),
    }

    try {
      mixpanel.track(generatedEvent, mixpanelProperties)
    } catch (error) {
      console.error(`Error occured while tracking mixpanel event, ${error}`)
    }
  }

  return <AnalyticsContext.Provider value={{ trackEvent }}>{children}</AnalyticsContext.Provider>
}

export const useAnalyticsTrackEvent = () => useContext(AnalyticsContext)
